import {
    useCallback,
    useRef,
    KeyboardEvent,
    DragEvent,
    useState,
    useEffect,
    useMemo,
    MouseEvent as ReactMouseEvent,
    Dispatch,
    SetStateAction,
    type ReactNode,
} from 'react';
import {
    ReactFlow,
    ReactFlowProvider,
    Edge,
    reconnectEdge,
    Connection,
    ReactFlowInstance,
    MarkerType,
    Controls,
    OnConnectStartParams,
    Node,
    addEdge,
    useUpdateNodeInternals,
    applyNodeChanges,
    applyEdgeChanges,
    NodeChange,
    EdgeChange,
    MiniMap,
    Background,
} from '@xyflow/react';
import {
    applyCallNodePromptSubLabel,
    getEdgeStyle,
    getNodeTypeLabelForDisplay,
    sanitizeWorkflowEdges,
    stripCrossTypeNodeDataFields,
} from '../../workflowNodeUtils';
import {
    NodeData,
    NodeType,
    CallNodeData,
    SMSNodeData,
    EmailNodeData,
    DelayNodeData,
    DecisionNodeData,
    DecisionCondition,
    DecisionConditionFormRow,
    EndNodeData,
    ServerNode,
} from '../../WorkflowBuilder.types';
import { buildBranchNodeLabel, buildDecisionNodeSubLabel } from '../../decisionNodeLabels';
import {
    DefaultNode,
    CallNode,
    SMSNode,
    EmailNode,
    DelayNode,
    DecisionNode,
    EndNode,
    TerminalBridgeNode,
} from '../../nodeTypes';
import NodeConfigModal from '../../NodeConfigModal';
import { useConnectionStartNode } from './hooks/useConnectionStartNode';
import { Modal, Button } from 'common/components';
import { useInitialNodesEdges } from './hooks/useInitialNodesEdges';
import { buildDualCanvasProjection, TERMINAL_FLOW_BRIDGE_ID, isTerminalFlowBridgeId } from '../../terminalCanvasSplit';
import { ChevronDown, ChevronRight } from 'lucide-react';

import '@xyflow/react/dist/style.css';
import styles from '../../AgenticWorkflowBuilder.module.scss';

const nodeTypes = {
    defaultNode: DefaultNode,
    callNode: CallNode,
    smsNode: SMSNode,
    emailNode: EmailNode,
    delayNode: DelayNode,
    decisionNode: DecisionNode,
    endNode: EndNode,
    terminalBridgeNode: TerminalBridgeNode,
};

/** Per-flow store: call the returned updater for nodes that belong to that React Flow instance. */
type FlowInternalsUpdater = (ids: string[]) => void;

function FlowInternalsBridge({
    children,
    onInternalsReady,
}: {
    children: ReactNode;
    onInternalsReady: (update: FlowInternalsUpdater) => void;
}) {
    const update = useUpdateNodeInternals();
    useEffect(() => {
        onInternalsReady((ids) => {
            for (const id of ids) {
                update(id);
            }
        });
    }, [update, onInternalsReady]);
    return <>{children}</>;
}

interface WorkflowCanvasProps {
    workflowDefinitionNodes: ServerNode[];
    counterRef: React.RefObject<number>;
    nodes: Node<NodeData>[];
    setNodes: Dispatch<SetStateAction<Node<NodeData>[]>>;
    edges: Edge<NodeData>[];
    setEdges: Dispatch<SetStateAction<Edge<NodeData>[]>>;
    terminalDecisionNodeId?: string | null;
    reactFlowId?: string;
    isEditWorkflow?: boolean;
}

export const WorkflowCanvas = ({
    workflowDefinitionNodes,
    counterRef,
    nodes,
    setNodes,
    edges,
    setEdges,
    terminalDecisionNodeId = null,
    reactFlowId = 'workflow-main',
    isEditWorkflow = false,
}: WorkflowCanvasProps) => {
    const reactFlowWrapper = useRef<HTMLDivElement>(null);
    const [reactFlowInstanceMain, setReactFlowInstanceMain] = useState<ReactFlowInstance<
        Node<NodeData>,
        Edge<NodeData>
    > | null>(null);
    const [hoveredEdge, setHoveredEdge] = useState<string | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [nodeToDelete, setNodeToDelete] = useState<string | null>(null);
    const [selectedNode, setSelectedNode] = useState<Node<NodeData> | null>(null);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [terminalFlowExpanded, setTerminalFlowExpanded] = useState(true);

    const { connectionStartNode, setConnectionStartNode } = useConnectionStartNode();

    const mainInternalsRef = useRef<FlowInternalsUpdater | null>(null);
    const terminalInternalsRef = useRef<FlowInternalsUpdater | null>(null);

    const registerMainInternals = useCallback((fn: FlowInternalsUpdater) => {
        mainInternalsRef.current = fn;
    }, []);

    const registerTerminalInternals = useCallback((fn: FlowInternalsUpdater) => {
        terminalInternalsRef.current = fn;
    }, []);

    const flushNodeInternals = useCallback((ids: string[]) => {
        const tids = terminalIdsRef.current;
        const mainIds = ids.filter((id) => !tids.has(id));
        const termIds = ids.filter((id) => tids.has(id));
        if (mainIds.length) mainInternalsRef.current?.(mainIds);
        if (termIds.length) terminalInternalsRef.current?.(termIds);
    }, []);

    const nodesRef = useRef(nodes);
    nodesRef.current = nodes;

    const dualProjection = useMemo(
        () => buildDualCanvasProjection(nodes, edges, terminalDecisionNodeId),
        [nodes, edges, terminalDecisionNodeId]
    );
    const terminalIdsRef = useRef(dualProjection.terminalIds);
    terminalIdsRef.current = dualProjection.terminalIds;

    const splitTerminalUi = Boolean(terminalDecisionNodeId && dualProjection.terminalNodes.length > 0);

    const getId = (): string => {
        const newId = counterRef.current + 1;
        counterRef.current = newId;
        return `node_${newId}`;
    };

    /** Stable identity for graph structure (ids only). Do not use `nodes` in layout effects — it causes infinite dock loops. */
    const nodesStructureKey = nodes
        .map((n) => n.id)
        .sort()
        .join('|');

    /** Drop invalid edges (bridge-only, missing nodes) after load. */
    useEffect(() => {
        setEdges((eds) => sanitizeWorkflowEdges(eds, nodesRef.current));
    }, [nodesStructureKey, edges.length, setEdges]);

    const onReactFlowInitMain = useCallback((instance: ReactFlowInstance<Node<NodeData>, Edge<NodeData>>) => {
        setReactFlowInstanceMain(instance);
    }, []);

    const onNodesChangeMain = useCallback(
        (changes: NodeChange<Node<NodeData>>[]) => {
            setNodes((nds) => {
                const tids = terminalIdsRef.current;
                const filtered = changes.filter((ch) => {
                    if (!('id' in ch) || ch.id == null) return true;
                    if (tids.has(ch.id)) return false;
                    if (ch.id === TERMINAL_FLOW_BRIDGE_ID && ch.type === 'position') return false;
                    // Bridge is not in parent `nodes`; ignore dimension churn so state does not thrash.
                    if (ch.id === TERMINAL_FLOW_BRIDGE_ID && ch.type === 'dimensions') return false;
                    return true;
                });
                return applyNodeChanges(filtered, nds);
            });
        },
        [setNodes]
    );

    const onNodesChangeTerminal = useCallback(
        (changes: NodeChange<Node<NodeData>>[]) => {
            setNodes((nds) => {
                const tids = terminalIdsRef.current;
                const filtered = changes.filter((ch) => {
                    if (!('id' in ch) || ch.id == null) return true;
                    return tids.has(ch.id);
                });
                return applyNodeChanges(filtered, nds);
            });
        },
        [setNodes]
    );

    const onEdgesChangeWrapped = useCallback(
        (changes: EdgeChange<Edge<NodeData>>[]) => {
            setEdges((eds) => applyEdgeChanges(changes, eds));
        },
        [setEdges]
    );

    // Update the deleteNode function to use our custom modal
    const deleteNode = useCallback((nodeId: string, e?: ReactMouseEvent): void | boolean => {
        // Show custom confirmation modal instead of window.confirm
        setNodeToDelete(nodeId);
        setShowDeleteModal(true);

        // Stop event propagation if this is being called from a click handler
        if (e && e.stopPropagation) {
            e.stopPropagation();
        }

        // Return false to indicate event should not propagate
    }, []);

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        // Check if Delete or Backspace was pressed
        if (event.key === 'Delete' || event.key === 'Backspace') {
            // Get selected edges
            const selectedEdges = edges.filter((edge) => edge.selected);

            if (selectedEdges.length > 0) {
                // Show confirmation dialog
                const isConfirmed = window.confirm('Are you sure you want to delete the selected connection(s)?');

                // Only delete if user confirms
                if (isConfirmed) {
                    setEdges(edges.filter((edge) => !edge.selected));
                }
            }
        }
    };

    const onDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';

        // Add a visual indicator that the canvas is active for dropping
        if (reactFlowWrapper.current) {
            reactFlowWrapper.current.classList.add(styles.dragActive);
        }
    }, []);

    // Add dragLeave handler to remove the drag-active class
    const onDragLeave = useCallback(() => {
        if (reactFlowWrapper.current) {
            reactFlowWrapper.current.classList.remove(styles.dragActive);
        }
    }, []);

    const onDrop = useCallback(
        (event: DragEvent<HTMLDivElement>) => {
            event.preventDefault();

            // Remove drag indicator
            if (reactFlowWrapper.current) {
                reactFlowWrapper.current.classList.remove(styles.dragActive);
            }

            if (!reactFlowInstanceMain || !reactFlowWrapper.current) return;

            const type = event.dataTransfer.getData('application/reactflow');
            if (!type) return;

            // Convert screen coordinates to flow coordinates (v12: screenToFlowPosition replaces project)
            const position = reactFlowInstanceMain.screenToFlowPosition({
                x: event.clientX,
                y: event.clientY,
            });

            // Snap to grid if enabled
            const snappedPosition = {
                x: Math.round(position.x / 15) * 15,
                y: Math.round(position.y / 15) * 15,
            };

            const newNodeId = getId();

            // Initialize node data based on type
            let initialData: NodeData;
            switch (type) {
                case 'callNode':
                    initialData = {
                        label: `${getNodeTypeLabelForDisplay(type)}`,
                        subLabel: 'AI Prompt: Select a prompt',
                        onDelete: deleteNode,
                        promptId: '',
                        promptName: '',
                        callText: '',
                    } as CallNodeData;
                    break;
                case 'smsNode':
                    initialData = {
                        label: `${getNodeTypeLabelForDisplay(type)}`,
                        subLabel: 'Static',
                        onDelete: deleteNode,
                        smsMode: 'static',
                        messageContent: '',
                        langfusePromptId: '',
                    } as SMSNodeData;
                    break;
                case 'emailNode':
                    initialData = {
                        label: `${getNodeTypeLabelForDisplay(type)}`,
                        subLabel: '',
                        onDelete: deleteNode,
                        messageContent: '',
                    } as EmailNodeData;
                    break;
                case 'delayNode':
                    initialData = {
                        label: `${getNodeTypeLabelForDisplay(type)}`,
                        subLabel: '',
                        onDelete: deleteNode,
                        delayType: 'TIMESTAMP',
                        specificDateTime: new Date().toISOString(),
                        timeValue: '30',
                        timeUnit: 'minutes',
                    } as DelayNodeData;
                    break;
                case 'decisionNode':
                    initialData = {
                        label: 'Decision',
                        subLabel: 'No conditions',
                        onDelete: deleteNode,
                        conditions: [],
                        defaultNextNode: null,
                    } as DecisionNodeData;
                    break;
                case 'endNode':
                    initialData = {
                        label: 'End',
                        subLabel: '',
                        onDelete: deleteNode,
                    } as EndNodeData;
                    break;
                default:
                    initialData = {
                        label: `${getNodeTypeLabelForDisplay(type)}`,
                        subLabel: '',
                        onDelete: deleteNode,
                    } as NodeData;
            }

            const newNode: Node<NodeData> = {
                id: newNodeId,
                type,
                position: snappedPosition,
                data: initialData,
            };

            setNodes((nds) => nds.concat(newNode));

            // Open config modal only for nodes that have config (no modal for End node)
            if (type !== 'endNode') {
                setTimeout(() => {
                    setSelectedNode(newNode);
                    setShowModal(true);
                }, 50);
            }
        },
        [reactFlowInstanceMain, setNodes, deleteNode]
    );

    const onReconnect = useCallback(
        (oldEdge: Edge<NodeData>, newConnection: Connection) => {
            setEdges((els) => reconnectEdge(oldEdge, newConnection, els) as Edge<NodeData>[]);
        },
        [setEdges]
    );

    // Edge mouse enter/leave handlers for hover effect
    const onEdgeMouseEnter = useCallback((event: React.MouseEvent, edge: Edge) => {
        setHoveredEdge(edge.id);
    }, []);

    const onEdgeMouseLeave = useCallback((event: React.MouseEvent, edge: Edge) => {
        setHoveredEdge(null);
    }, []);

    const onNodeClick = useCallback((event: React.MouseEvent, node: Node<NodeData>) => {
        if ((event.target as HTMLElement).closest('.deleteNodeButton')) {
            return;
        }
        if (isTerminalFlowBridgeId(node.id)) {
            return;
        }
        // End node has no config – do not open modal
        if (node.type === 'endNode') {
            return;
        }
        setSelectedNode(node);
        setShowModal(true);
    }, []);

    const onEdgeClick = useCallback(
        (event: React.MouseEvent, edge: Edge) => {
            // Show confirmation dialog
            const isConfirmed = window.confirm('Are you sure you want to delete this connection?');

            // Only delete if user confirms
            if (isConfirmed) {
                setEdges((eds) => eds.filter((e) => e.id !== edge.id));
            }
        },
        [setEdges]
    );

    const onConnectStart = useCallback((_: any, params: OnConnectStartParams) => {
        setConnectionStartNode(params);
    }, []);

    const addEdgeFromConnection = useCallback(
        (params: Connection, remapBridgeTarget: boolean) => {
            if (params.source === params.target) {
                console.warn('Self-loops are not allowed. Cannot connect a node to itself.');
                return;
            }
            let target = params.target;
            if (remapBridgeTarget && target === TERMINAL_FLOW_BRIDGE_ID && terminalDecisionNodeId) {
                target = terminalDecisionNodeId;
            }
            setEdges((eds) =>
                addEdge(
                    {
                        ...params,
                        target,
                        type: 'straight',
                        style: getEdgeStyle({ id: `e_${params.source}_${target}`, selected: false }, hoveredEdge),
                        selected: false,
                        markerEnd: {
                            type: MarkerType.ArrowClosed,
                        },
                    },
                    eds
                )
            );
        },
        [setEdges, hoveredEdge, terminalDecisionNodeId]
    );

    const onConnectMain = useCallback(
        (params: Connection) => addEdgeFromConnection(params, true),
        [addEdgeFromConnection]
    );

    const onConnectTerminal = useCallback(
        (params: Connection) => addEdgeFromConnection(params, false),
        [addEdgeFromConnection]
    );

    const onConnectEnd = useCallback(
        (event: MouseEvent | TouchEvent) => {
            if (!connectionStartNode || !reactFlowInstanceMain) return;

            const clientX = 'touches' in event ? event.touches[0].clientX : (event as MouseEvent).clientX;
            const clientY = 'touches' in event ? event.touches[0].clientY : (event as MouseEvent).clientY;

            const targetNodesUnderMouse = document
                .elementsFromPoint(clientX, clientY)
                .filter((el) => el.classList.contains('react-flow__node'));

            if (targetNodesUnderMouse.length > 0) {
                const targetNodeId = targetNodesUnderMouse[0].getAttribute('data-id');
                const targetNode = nodes.find((n) => n.id === targetNodeId);

                if (
                    targetNode &&
                    targetNode.id !== connectionStartNode.nodeId &&
                    connectionStartNode.nodeId &&
                    targetNodeId
                ) {
                    const newConnection: Connection = {
                        source: connectionStartNode.nodeId,
                        sourceHandle: connectionStartNode.handleId || null,
                        target: targetNodeId,
                        targetHandle: null,
                    };
                    addEdgeFromConnection(newConnection, targetNodeId === TERMINAL_FLOW_BRIDGE_ID);
                }
            }

            setConnectionStartNode(null);
        },
        [connectionStartNode, reactFlowInstanceMain, nodes, addEdgeFromConnection]
    );

    // Add a function to handle the actual deletion after confirmation
    const confirmDeleteNode = (): void => {
        if (!nodeToDelete) return;
        if (isTerminalFlowBridgeId(nodeToDelete)) {
            setShowDeleteModal(false);
            setNodeToDelete(null);
            return;
        }

        const idToDelete = nodeToDelete;

        setNodes((nds) => {
            const node = nds.find((n) => n.id === idToDelete);
            const idsToRemove = new Set<string>([idToDelete]);
            if (node?.type === 'decisionNode') {
                const data = node.data as DecisionNodeData;
                (data.conditions || []).forEach((c) => {
                    if (c.nextNode) idsToRemove.add(c.nextNode);
                });
                if (data.defaultNextNode) idsToRemove.add(data.defaultNextNode);
            }

            const updated = nds.map((n) => {
                if (n.type === 'decisionNode' && n.id !== idToDelete) {
                    const data = n.data as DecisionNodeData;
                    const conds = data.conditions || [];
                    const newConditions = conds.filter((c) => c.nextNode !== idToDelete);
                    let newDefault = data.defaultNextNode;
                    if (newDefault === idToDelete) newDefault = null;
                    if (newConditions.length !== conds.length || newDefault !== data.defaultNextNode) {
                        return {
                            ...n,
                            data: {
                                ...data,
                                conditions: newConditions,
                                defaultNextNode: newDefault,
                                subLabel: buildDecisionNodeSubLabel(newConditions),
                            } as DecisionNodeData,
                        };
                    }
                }
                return n;
            });
            return updated.filter((n) => !idsToRemove.has(n.id));
        });

        setEdges((eds) => {
            const node = nodes.find((n) => n.id === idToDelete);
            const idsToRemove = new Set<string>([idToDelete]);
            if (node?.type === 'decisionNode') {
                const data = node.data as DecisionNodeData;
                (data.conditions || []).forEach((c) => {
                    if (c.nextNode) idsToRemove.add(c.nextNode);
                });
                if (data.defaultNextNode) idsToRemove.add(data.defaultNextNode);
            }
            return eds.filter((e) => !idsToRemove.has(e.source) && !idsToRemove.has(e.target));
        });

        setShowDeleteModal(false);
        setNodeToDelete(null);
    };

    // Add a function to cancel deletion
    const cancelDeleteNode = (): void => {
        setShowDeleteModal(false);
        setNodeToDelete(null);
    };

    const nodeTypeToNextNodeType = useCallback(
        (nodeType: string | undefined): 'CALL' | 'SMS' | 'DELAY' | 'END' | null => {
            if (nodeType === 'callNode') return 'CALL';
            if (nodeType === 'smsNode' || nodeType === 'emailNode') return 'SMS';
            if (nodeType === 'delayNode') return 'DELAY';
            if (nodeType === 'endNode') return 'END';
            return null;
        },
        []
    );

    const onDecisionDone = useCallback(
        (
            nodeId: string,
            rows: DecisionConditionFormRow[],
            defaultNextNodeType: 'CALL' | 'SMS' | 'DELAY' | 'END' | null
        ) => {
            const decisionNode = nodes.find((n) => n.id === nodeId);
            if (!decisionNode || decisionNode.type !== 'decisionNode') return;

            const existingData = decisionNode.data as DecisionNodeData;
            const existingConditions = existingData.conditions || [];
            const existingDefault = existingData.defaultNextNode ?? null;

            const nextTypeToReactFlow: Record<'CALL' | 'SMS' | 'DELAY' | 'END', string> = {
                CALL: 'callNode',
                SMS: 'smsNode',
                DELAY: 'delayNode',
                END: 'endNode',
            };

            const branchParentId = decisionNode.parentId;
            const baseX = decisionNode.position.x;
            const baseY = decisionNode.position.y + 120;
            const horizontalGap = 100;

            const createBranchNodeData = (reactFlowNodeType: string, label: string): NodeData => {
                const base = { label, subLabel: '', onDelete: deleteNode };
                if (reactFlowNodeType === 'callNode') {
                    return {
                        ...base,
                        subLabel: 'AI Prompt: Select a prompt',
                        promptId: '',
                        promptName: '',
                        callText: '',
                    } as CallNodeData;
                }
                if (reactFlowNodeType === 'smsNode') {
                    return { ...base, smsMode: 'static', messageContent: '', langfusePromptId: '' } as SMSNodeData;
                }
                if (reactFlowNodeType === 'endNode') {
                    return { ...base } as EndNodeData;
                }
                return {
                    ...base,
                    delayType: 'TIMESTAMP',
                    specificDateTime: new Date().toISOString(),
                    timeValue: '30',
                    timeUnit: 'minutes',
                } as DelayNodeData;
            };

            const createDecisionEdge = (sourceHandle: string, targetId: string): Edge<NodeData> => ({
                id: `e_${nodeId}_${sourceHandle}_${targetId}`,
                source: nodeId,
                sourceHandle,
                target: targetId,
                targetHandle: null,
                type: 'straight',
                markerEnd: { type: MarkerType.ArrowClosed },
            });

            const oldTargetIds = new Set<string>();

            if (rows.length === 0 && !defaultNextNodeType) {
                existingConditions.forEach((c) => {
                    if (c.nextNode) oldTargetIds.add(c.nextNode);
                });
                if (existingDefault) oldTargetIds.add(existingDefault);
                setNodes((nds) =>
                    nds
                        .filter((n) => !oldTargetIds.has(n.id))
                        .map((n) =>
                            n.id === nodeId && n.type === 'decisionNode'
                                ? {
                                      ...n,
                                      data: {
                                          ...n.data,
                                          conditions: [],
                                          defaultNextNode: null,
                                          subLabel: buildDecisionNodeSubLabel([]),
                                      } as DecisionNodeData,
                                  }
                                : n
                        )
                );
                setEdges((eds) => eds.filter((e) => e.source !== nodeId));
                requestAnimationFrame(() => flushNodeInternals([nodeId]));
                return;
            }

            for (let i = rows.length; i < existingConditions.length; i++) {
                const t = existingConditions[i]?.nextNode;
                if (t) oldTargetIds.add(t);
            }
            if (existingDefault != null && !defaultNextNodeType) {
                oldTargetIds.add(existingDefault);
            }

            const resolveTarget = (
                existingId: string | undefined,
                desiredNextType: 'CALL' | 'SMS' | 'DELAY' | 'END',
                positionIndex: number,
                sourceHandle: string,
                branchLabel: string
            ): { targetId: string; node?: Node<NodeData>; edge: Edge<NodeData> } => {
                const reactFlowNodeType = nextTypeToReactFlow[desiredNextType];
                const existingNode = existingId ? nodes.find((n) => n.id === existingId) : undefined;
                const canReuse = existingNode && nodeTypeToNextNodeType(existingNode.type) === desiredNextType;

                if (canReuse && existingId) {
                    return { targetId: existingId, edge: createDecisionEdge(sourceHandle, existingId) };
                }
                if (existingId) oldTargetIds.add(existingId);
                const targetId = getId();
                const newBranchNode: Node<NodeData> = {
                    id: targetId,
                    type: reactFlowNodeType,
                    parentId: branchParentId,
                    extent: branchParentId ? 'parent' : undefined,
                    position: { x: baseX + positionIndex * horizontalGap, y: baseY },
                    data: createBranchNodeData(reactFlowNodeType, branchLabel),
                };
                return {
                    targetId,
                    node: newBranchNode,
                    edge: createDecisionEdge(sourceHandle, targetId),
                };
            };

            const newNodes: Node<NodeData>[] = [];
            const newEdges: Edge<NodeData>[] = [];
            const newConditions: DecisionCondition[] = [];

            rows.forEach((row, index) => {
                const existingTargetId = existingConditions[index]?.nextNode;
                const branchLabel = buildBranchNodeLabel(row.nextNodeType, row.decisionField, row.values, false);
                const resolved = resolveTarget(
                    existingTargetId,
                    row.nextNodeType,
                    index,
                    `condition-${index}`,
                    branchLabel
                );
                if (resolved.node) newNodes.push(resolved.node);
                newEdges.push(resolved.edge);
                newConditions.push({
                    decisionField: row.decisionField,
                    values: [...row.values],
                    nextNode: resolved.targetId,
                });
            });

            let defaultNextNodeResult: string | null = null;
            if (defaultNextNodeType) {
                const defLabel = buildBranchNodeLabel(defaultNextNodeType, 'call_disposition', [], true);
                const resolved = resolveTarget(
                    existingDefault ?? undefined,
                    defaultNextNodeType,
                    rows.length,
                    'default',
                    defLabel
                );
                if (resolved.node) newNodes.push(resolved.node);
                newEdges.push(resolved.edge);
                defaultNextNodeResult = resolved.targetId;
            }

            const newNodeIds = new Set(newNodes.map((n) => n.id));
            const branchNodePositions = new Map<string, { x: number; y: number }>();

            const existingBranchIds = new Set(
                [...newConditions.map((c) => c.nextNode), defaultNextNodeResult].filter(
                    (id): id is string => !!id && !newNodeIds.has(id)
                )
            );
            const maxExistingX =
                existingBranchIds.size > 0
                    ? Math.max(...nodes.filter((n) => existingBranchIds.has(n.id)).map((n) => n.position.x))
                    : baseX - horizontalGap;
            let newNodeIndex = 0;
            rows.forEach((row, idx) => {
                const targetId = newConditions[idx]?.nextNode;
                if (targetId && newNodeIds.has(targetId)) {
                    branchNodePositions.set(targetId, {
                        x: maxExistingX + (newNodeIndex + 1) * horizontalGap,
                        y: baseY,
                    });
                    newNodeIndex += 1;
                }
            });
            if (defaultNextNodeResult && newNodeIds.has(defaultNextNodeResult)) {
                branchNodePositions.set(defaultNextNodeResult, {
                    x: maxExistingX + (newNodeIndex + 1) * horizontalGap,
                    y: baseY,
                });
            }

            setNodes((nds) => {
                const withoutOldBranchNodes = nds.filter((n) => !oldTargetIds.has(n.id));
                const updated = withoutOldBranchNodes.map((n) => {
                    if (n.id === nodeId && n.type === 'decisionNode') {
                        return {
                            ...n,
                            data: {
                                ...n.data,
                                conditions: newConditions,
                                defaultNextNode: defaultNextNodeResult,
                                subLabel: buildDecisionNodeSubLabel(newConditions),
                            } as DecisionNodeData,
                        };
                    }
                    const newPosition = branchNodePositions.get(n.id);
                    return newPosition ? { ...n, position: newPosition } : n;
                });
                const newNodesWithPosition = newNodes.map((n) => {
                    const pos = branchNodePositions.get(n.id);
                    return pos ? { ...n, position: pos } : n;
                });
                return [...updated, ...newNodesWithPosition];
            });

            const edgesToSet: Edge<NodeData>[] = newEdges.map((e) => ({
                ...e,
                style: getEdgeStyle({ ...e, id: e.id, selected: false }, null),
                selected: false,
                markerEnd: e.markerEnd ?? { type: MarkerType.ArrowClosed },
            }));

            setEdges((eds) =>
                eds
                    .filter((e) => e.source !== nodeId && !oldTargetIds.has(e.source) && !oldTargetIds.has(e.target))
                    .concat(edgesToSet)
            );

            const nodeIdsToUpdate = [
                nodeId,
                ...newConditions.map((c) => c.nextNode),
                ...(defaultNextNodeResult ? [defaultNextNodeResult] : []),
            ];
            requestAnimationFrame(() => {
                flushNodeInternals(nodeIdsToUpdate);
            });
        },
        [nodes, setNodes, setEdges, deleteNode, getId, nodeTypeToNextNodeType, flushNodeInternals]
    );

    const handleUpdateNode = (id: string, updates: Partial<NodeData>): void => {
        setNodes((nds) =>
            nds.map((n) => {
                if (n.id !== id) return n;

                const mergedUpdates = { ...updates };
                if (n.type === 'callNode') {
                    applyCallNodePromptSubLabel(mergedUpdates);
                }

                const newData: Record<string, unknown> = { ...n.data, ...mergedUpdates };
                stripCrossTypeNodeDataFields(n.type as NodeType | undefined, newData);

                return { ...n, data: newData as NodeData };
            })
        );
        setShowModal(false);
        setSelectedNode(null);
    };

    useInitialNodesEdges(true, workflowDefinitionNodes, setNodes, setEdges, deleteNode);

    const nodeClassName = (node: Node<NodeData>): string => node.type || '';

    const minimapNodeColor = useCallback((node: Node<NodeData>) => {
        if (isTerminalFlowBridgeId(node.id)) {
            return 'rgba(0,0,0,0)';
        }
        return '#cbd5e1';
    }, []);

    const flowSharedProps = {
        nodeTypes,
        onEdgeMouseEnter,
        onEdgeMouseLeave,
        onEdgesChange: onEdgesChangeWrapped,
        onReconnect,
        onNodeClick,
        onEdgeClick,
        snapToGrid: true,
        snapGrid: [10, 10] as [number, number],
        zIndexMode: 'auto' as const,
        defaultViewport: { x: 0, y: 0, zoom: 1 },
        deleteKeyCode: ['Backspace', 'Delete'] as string[],
        onConnectStart,
        onConnectEnd,
        zoomOnScroll: false,
    };

    const mainFlow = (
        <ReactFlowProvider>
            <FlowInternalsBridge onInternalsReady={registerMainInternals}>
                <ReactFlow<Node<NodeData>, Edge<NodeData>>
                    {...flowSharedProps}
                    id={reactFlowId}
                    nodes={dualProjection.mainNodes}
                    edges={dualProjection.mainEdges}
                    onNodesChange={onNodesChangeMain}
                    onConnect={onConnectMain}
                    onInit={onReactFlowInitMain}
                    fitView={isEditWorkflow}
                >
                    <Controls />
                    <MiniMap pannable nodeClassName={nodeClassName} nodeColor={minimapNodeColor} />
                    <Background />
                </ReactFlow>
            </FlowInternalsBridge>
        </ReactFlowProvider>
    );

    const terminalFlow = splitTerminalUi ? (
        <ReactFlowProvider>
            <FlowInternalsBridge onInternalsReady={registerTerminalInternals}>
                <ReactFlow<Node<NodeData>, Edge<NodeData>>
                    {...flowSharedProps}
                    id={`${reactFlowId}-terminal`}
                    nodes={dualProjection.terminalNodes}
                    edges={dualProjection.terminalEdges}
                    onNodesChange={onNodesChangeTerminal}
                    onConnect={onConnectTerminal}
                    fitView={false}
                >
                    <Controls />
                    <MiniMap pannable nodeClassName={nodeClassName} nodeColor={minimapNodeColor} />
                    <Background />
                </ReactFlow>
            </FlowInternalsBridge>
        </ReactFlowProvider>
    ) : null;

    /** Drag/drop targets the wrapper in single-canvas mode; split mode uses `#workflow-main-flow-panel` instead. */
    const rootDropZoneProps = splitTerminalUi ? {} : { ref: reactFlowWrapper, onDrop, onDragOver, onDragLeave };

    return (
        <div
            className={splitTerminalUi ? styles.canvasSplit : styles.canvas}
            onKeyDown={onKeyDown}
            tabIndex={0}
            {...rootDropZoneProps}
        >
            {splitTerminalUi ? (
                <>
                    <div className={styles.canvasMainPane}>
                        <div className={styles.canvasMainTitle}>Non Terminal Transition Handler</div>
                        <div
                            id='workflow-main-flow-panel'
                            ref={reactFlowWrapper}
                            onDrop={onDrop}
                            onDragOver={onDragOver}
                            onDragLeave={onDragLeave}
                            className={styles.canvasMainFlow}
                        >
                            {mainFlow}
                        </div>
                    </div>
                    <div
                        className={
                            terminalFlowExpanded
                                ? styles.canvasTerminalPane
                                : `${styles.canvasTerminalPane} ${styles.canvasTerminalPaneCollapsed}`
                        }
                    >
                        <button
                            type='button'
                            className={styles.canvasTerminalTitle}
                            onClick={() => setTerminalFlowExpanded((v) => !v)}
                            aria-expanded={terminalFlowExpanded}
                            aria-controls='workflow-terminal-flow-panel'
                            title={
                                terminalFlowExpanded
                                    ? 'Hide Terminal Transition Handler'
                                    : 'Show Terminal Transition Handler'
                            }
                        >
                            <span className={styles.canvasTerminalTitleIndicator} aria-hidden>
                                {terminalFlowExpanded ? (
                                    <ChevronDown size={18} strokeWidth={2.25} />
                                ) : (
                                    <ChevronRight size={18} strokeWidth={2.25} />
                                )}
                            </span>
                            <span>Terminal Transition Handler</span>
                        </button>
                        <div
                            id='workflow-terminal-flow-panel'
                            className={
                                terminalFlowExpanded
                                    ? styles.canvasTerminalFlow
                                    : `${styles.canvasTerminalFlow} ${styles.canvasTerminalFlowHidden}`
                            }
                        >
                            {terminalFlow}
                        </div>
                    </div>
                </>
            ) : (
                mainFlow
            )}

            {/* Node Delete Confirmation Modal */}
            <Modal
                isOpen={showDeleteModal}
                onClose={cancelDeleteNode}
                title='Delete Node'
                size='sm'
                showDefaultFooter={false}
                footer={
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                        <Button className='configCancelButton' onClick={cancelDeleteNode}>
                            Cancel
                        </Button>
                        <Button
                            className='configSaveButton'
                            style={{ backgroundColor: '#f44336' }}
                            onClick={confirmDeleteNode}
                        >
                            Delete Node
                        </Button>
                    </div>
                }
            >
                <p style={{ margin: '10px 0' }}>
                    Are you sure you want to delete this node? This action cannot be undone.
                </p>
            </Modal>

            {/* Add back the NodeConfigModal */}
            {showModal && selectedNode && (
                <NodeConfigModal
                    node={selectedNode}
                    nodes={nodes}
                    terminalDecisionNodeId={terminalDecisionNodeId}
                    onClose={() => setShowModal(false)}
                    onSave={handleUpdateNode}
                    onDecisionDone={selectedNode.type === 'decisionNode' ? onDecisionDone : undefined}
                />
            )}
        </div>
    );
};
